(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (global = global || self, factory(global['q-utils'] = {}));
}(this, (function (exports) { 'use strict';

  /**
   * Transform an HTMLCollection, HTMLElement, DOMString into an Array of HTMLElement
   *
   * @param {(HTMLElement|HTMLCollection|Array|DOMString)} elements
   *
   * @returns {Array<HTMLElement>} elements
   */
  const preprocessElementsArgument = elements => {
    let elementsArray = null;

    if (typeof elements === 'string') {
      elementsArray = Array.from(document.querySelectorAll(elements));
    } else if (elements instanceof window.HTMLCollection) {
      elementsArray = Array.from(elements);
    } else if (elements instanceof window.HTMLElement || elements === window || elements === document) {
      elementsArray = [elements];
    } else if (Array.isArray(elements)) {
      elementsArray = elements;
    }

    if (!elementsArray || elementsArray.length === 0) return null

    return elementsArray
  };

  /**
   * Add class to an HTML element or an array of HTML elements
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} elements - HTML element or an array of HTML elements where the class will be added
   * @param {(String|Array<String>)} className - Class name to add
   */
  var addClass = (elements, className) => {
    elements = preprocessElementsArgument(elements);

    if (!elements) return

    if (typeof className === 'string') {
      className = [className];
    }

    elements.forEach(e => e.classList.add(...className));
  };

  /**
   * Dom events callback
   *
   * @callback domEventCallbackType
   * @param {Object} [e] - event informations
   */

  /**
   * Add a listener to element(s)
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} elements - HTML element or an array of HTML elements where the event is added
   * @param {(String|Array<String>)} eventName - Event to add
   * @param {domEventCallbackType} cb
   */
  const addEvent = (elements, eventName, cb) => {
    elements = preprocessElementsArgument(elements);

    if (!elements) return

    if (typeof eventName === 'string') {
      elements.forEach(e => e.addEventListener(eventName, cb));
    } else if (Array.isArray(eventName)) {
      eventName.forEach(event => {
        addEvent(elements, event, cb);
      });
    }
  };

  /**
   * Get a clamped value between optional min and max parameters
   *
   * @param {Number} value - Value to clamp
   * @param {Number|null} min - Lower edge of the desired range
   * @param {Number|null} max - Upper edge of the desired range
   */
  var clamp = (value, min, max) => {
    if (min !== undefined && max !== undefined) {
      return Math.min(Math.max(value, min), max)
    } else if (min !== undefined) {
      return Math.max(value, min)
    } else if (max !== undefined) {
      return Math.min(value, max)
    } else {
      return value
    }
  };

  /**
   * Debounce function
   *
   * @param {Function} callback
   * @param {Number} wait
   */
  var debounce = (callback, wait) => {
    let timeout;

    return () => {
      window.clearTimeout(timeout);
      timeout = setTimeout(() => callback(), wait);
    }
  };

  /**
   * Select the all HTML elements which match with className in parent
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} parent - parent element
   * @param {String} className - class name
   *
   * @returns {(Array|null)} array of elements corresponding to class name or null
   */
  var gebc = (parent, className) => {
    parent = preprocessElementsArgument(parent);

    if (!parent) return []

    const domCollection = parent.reduce((acc, curr) => {
      acc.push(...curr.getElementsByClassName(className));

      return acc
    }, []);

    return domCollection
  };

  /**
   * Select the first HTML element which match with the id
   *
   * @param {String} id - id to find
   *
   * @returns {(HTMLElement|null)} HTML element corresponding to id or null
   */
  var gebi = (id) => {
    return document.getElementById(id)
  };

  /**
   * @typedef {Object} PositionObject
   * @property {number} top - The element's top value
   * @property {number} bottom - The element's bottom value
   * @property {number} left - The element's left value
   * @property {number} right - The element's right value
   * @property {number} width - The element's width value
   * @property {number} height - The element's height value
   * @property {Function} relativeTo - Function to get position relative to an other HTML element
   */

  /**
   *
   * @param {HTMLElement} elements - HTML element which you want the position
   *
   * @returns {PositionObject} Object containing all positions infos of the element
   */
  var getPos = (elements) => {
    const bounding = elements.getBoundingClientRect();

    return {
      top: bounding.top,
      bottom: bounding.bottom,
      left: bounding.left,
      right: bounding.right,
      width: bounding.width,
      height: bounding.height,
      relativeTo: (relative) => {
        if (relative === 'page') {
          return {
            top: bounding.top + window.pageYOffset,
            bottom: bounding.bottom + window.pageYOffset,
            left: bounding.left + window.pageXOffset,
            right: bounding.right + window.pageXOffset
          }
        } else {
          const relativeBounding = relative.getBoundingClientRect();
          return {
            top: bounding.top - relativeBounding.top,
            bottom: bounding.bottom - relativeBounding.bottom,
            left: bounding.left - relativeBounding.left,
            right: bounding.right - relativeBounding.right
          }
        }
      }
    }
  };

  /**
   * Check that the object contains all properties passed in arguments
   *
   * @param {Object} object - Object to check
   * @param {Array} properties - List of properties that the object has to contain
   */
  var hasProperties = (object, properties) => {
    return properties.every(property => object.hasOwnProperty(property))
  };

  /**
   * Check that the value is one of the values passed in arguments
   *
   * @param {any} value - Value to check
   * @param {Array} possibilities - List of values that the value should be equal
   */
  var isOneOf = (value, possibilities) => {
    return possibilities.indexOf(value) > -1
  };

  /**
   * Lerp
   *
   * @param {Number} value - Value to lerp
   * @param {Number} target - Value targeted
   * @param {Number} coeff - Coefficient between value and target
   */
  var lerp = (value, target, coeff) => {
    return value * (1 - coeff) + target * coeff
  };

  const LOG_PAD = 25;

  const createName = (tag, id = null, classes = []) => {
    let name = tag;

    if (id) {
      name += `#${id}`;
    }

    classes.forEach(className => {
      name += `.${className}`;
    });

    return name
  };

  /**
   * Log element(s) with tag, id, classes and dataset informations
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} elements - HTML elements to log
   * @param {Boolean=false} collapsed - Collapse log
   */
  var logElement = (elements, collapsed) => {
    elements = preprocessElementsArgument(elements);

    if (!elements) return

    const style = 'font-size:1.2em;font-weight:800';

    elements.forEach(element => {
      // Get all attributes
      const tag = element.tagName.toLowerCase();
      const id = element.id;
      const classes = Array.from(element.classList);
      const dataset = element.dataset;

      // Open group with a brief name
      const name = createName(tag, id, classes);
      if (collapsed) {
        console.groupCollapsed(name);
      } else {
        console.group(name);
      }

      // Log all attributes
      console.log('%celement'.padEnd(23, ' '), style, element);
      console.log('%ctag'.padEnd(LOG_PAD, ' '), style, tag);
      if (id) console.log('%cid'.padEnd(LOG_PAD, ' '), style, id);
      if (classes.length > 0) console.log('%cclasses'.padEnd(LOG_PAD, ' '), style, classes);
      if (Object.keys(dataset).length > 0) console.log('%cdataset'.padEnd(LOG_PAD, ' '), style, dataset);

      // Close group
      console.groupEnd();
    });
  };

  /**
   * Get a mapped number from starting range to the desired range, optionaly clamped
   *
   * @param {Number} value - value to map
   * @param {Number} start1 - Lower edge of the starting range
   * @param {Number} end1 - Upper edge of the starting range
   * @param {Number} start2 - Lower edge of the desired range
   * @param {Number} end2 - Upper edge of the desired range
   * @param {Boolean} clamped - Indicates if the value should be clamped
   */
  var map = (value, start1, end1, start2, end2, clamped = false) => {
    const mapped = ((value - start1) / (end1 - start1)) * (end2 - start2) + start2;
    return (clamped) ? clamp(mapped, start2, end2) : mapped
  };

  /**
   * Select the first HTML element which match with selector in parent
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} parent - parent element
   * @param {String} selector - selector
   *
   * @returns {(HTMLElement|null)} element corresponding to selector or null
   */
  var qs = (parent, selector) => {
    parent = preprocessElementsArgument(parent);

    if (!parent) return []

    const domCollection = parent.reduce((acc, curr) => {
      if (curr.querySelector(selector)) {
        acc.push(curr.querySelector(selector));
      }

      return acc
    }, []);

    if (parent.length === 1) return domCollection[0]

    return domCollection
  };

  /**
   * Select the all HTML elements which match with selector in parent
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} parent - parent element
   * @param {String} selector - selector
   *
   * @returns {(Array|null)} array of elements corresponding to selector or null
   */
  var qsa = (parent, selector) => {
    parent = preprocessElementsArgument(parent);

    if (!parent) return []

    const domCollection = parent.reduce((acc, curr) => {
      if (curr.querySelectorAll(selector)) {
        acc.push(...curr.querySelectorAll(selector));
      }

      return acc
    }, []);

    return domCollection
  };

  /**
   * Remove class to an HTML element or an array of HTML elements
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} elements - HTML element or an array of HTML elements where the class will be removed
   * @param {(String|Array<String>)} className - Class name to remove
   */
  var removeClass = (elements, className = null) => {
    elements = preprocessElementsArgument(elements);

    if (!elements) return

    if (typeof className === 'string') {
      className = [className];
    }

    elements.forEach(currentElement => className ? currentElement.classList.remove(...className) : currentElement.className = '');
  };

  /**
   * Remove a listener from element(s)
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} elements - HTML element or an array of HTML elements where the event is removed
   * @param {(String|Array)} eventName - Event name to remove
   * @param {domEventCallbackType} [cb=null] - C
   */
  const removeEvent = (elements, eventName, cb = null) => {
    elements = preprocessElementsArgument(elements);
    if (typeof eventName === 'string') {
      elements.forEach(e => e.removeEventListener(eventName, cb));
    } else if (Array.isArray(eventName)) {
      eventName.forEach(event => {
        removeEvent(elements, event, cb);
      });
    }
  };

  /**
   * Get all datas from a form element
   *
   * @param {HTMLFormElement} form - The form which will be serialized
   *
   * @returns {Object} Object containing all input values
   */
  var serializeForm = form => {
    form = preprocessElementsArgument(form);

    if (!form) return null

    return Object.values(form[0].elements).reduce((obj, field) => {
      // Submit
      if (field.type === 'submit') return obj

      // File
      if (field.type === 'file') {
        obj[field.name] = Array.from(field.files);

        return obj
      }

      // Checkbox
      if (field.type === 'checkbox') {
        obj[field.name] = field.checked;

        return obj
      }

      // Number
      if (field.type === 'number') {
        if (field.value.length === 0) {
          obj[field.name] = null;
        } else {
          obj[field.name] = parseFloat(field.value);
        }

        return obj
      }

      // Select
      if (field instanceof window.HTMLSelectElement) {
        if (field.multiple) {
          obj[field.name] = Array.from(field.selectedOptions).reduce((acc, option) => acc.concat(option.value), []);
        } else {
          obj[field.name] = field.selectedOptions[0].value;
        }

        return obj
      }

      obj[field.name] = field.value;

      return obj
    }, {})
  };

  /**
   * Set style to a single or a list of HTML elements
   *
   * @param {(HTMLElement|HTMLCollection|Array<HTMLElement>|DOMString)} el - HTML element or an array of HTML elements where the style is applied
   * @param {Object} styles - Styles applied
   */
  var setStyle = (elements, styles) => {
    elements = preprocessElementsArgument(elements);

    if (!elements) return

    elements.forEach(e => Object.assign(e.style, styles));
  };

  const cloneObject = obj => {
    const clone = {};

    Object.entries(obj)
      .forEach(([key, value]) => {
        if (value != null && typeof value === 'object') {
          clone[key] = cloneObject(value);
        } else {
          clone[key] = value;
        }
      });
    return clone
  };

  /**
   * Return a random integer number between min and max
   *
   * @param {Number} min - minimum number returned
   * @param {Number} max - maximum number returned
   *
   * @returns {Number} Random integer number between min and max
   */
  var randomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);

    return Math.floor(Math.random() * (max - min + 1)) + min
  };

  /**
   * Return a random float number between min and max
   *
   * @param {Number} min - minimum number returned
   * @param {Number} max - maximum number returned
   *
   * @returns {Number} Random float number between min and max
   */
  var randomFloat = (min, max) => {
    return parseFloat((Math.random() * (max - min) + min).toFixed(4))
  };

  exports.addClass = addClass;
  exports.addEvent = addEvent;
  exports.clamp = clamp;
  exports.debounce = debounce;
  exports.deepClone = cloneObject;
  exports.gebc = gebc;
  exports.gebi = gebi;
  exports.getPos = getPos;
  exports.hasProperties = hasProperties;
  exports.isOneOf = isOneOf;
  exports.lerp = lerp;
  exports.logElement = logElement;
  exports.map = map;
  exports.qs = qs;
  exports.qsa = qsa;
  exports.randomFloat = randomFloat;
  exports.randomInt = randomInt;
  exports.removeClass = removeClass;
  exports.removeEvent = removeEvent;
  exports.serializeForm = serializeForm;
  exports.setStyle = setStyle;

  Object.defineProperty(exports, '__esModule', { value: true });

})));



//////////////////
// WEBPACK FOOTER
// /var/www/html/wp-content/themes/globald/node_modules/@qneyraud/q-utils/umd/q-utils.js
// module id = 2
// module chunks = 0